// --- TOAST NOTIFICATIONS (Novo) ---
export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message: string;
}

// --- EXISTENTE ---

export enum PropertyStatus {
  Occupied = 'Occupied',
  Vacant = 'Vacant',
  Maintenance = 'Maintenance'
}

export enum InvestmentStrategy {
  BuyAndHold = 'Buy & Hold',
  FixAndFlip = 'Fix & Flip',
  ShortTerm = 'Short Term / Airbnb',
  BRRRR = 'BRRRR',
  Wholesale = 'Wholesale'
}

export type SubscriptionPlan = 'Starter' | 'Investor' | 'Pro';

export interface UserSubscription {
  plan: SubscriptionPlan;
  status: 'active' | 'canceled' | 'past_due';
  renewalDate: string;
  billingPeriod: 'monthly' | 'annual';
}

export interface User {
  id: string;
  name: string;
  email: string;
  subscription?: UserSubscription;
  emailVerified?: boolean;
}

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'annual';
  status: 'paid' | 'refunded';
}

// --- DOCUMENT VAULT ---
export interface AppDocument {
  id: string;
  parentId: string; // Property ID, Tenant ID, or 'global'
  parentType: 'property' | 'tenant' | 'deal' | 'entity';
  name: string;
  type: string; // MIME type
  size: number;
  data: Blob; // Stored in IndexedDB
  dateAdded: string;
}

// --- CALENDAR ---
export type EventType = 'Rent' | 'Lease' | 'Insurance' | 'Task' | 'Tax';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // YYYY-MM-DD
  type: EventType;
  relatedId?: string; // ID of property/task
  amount?: number;
  isCompleted?: boolean;
}

// --- ENTITY MANAGEMENT ---
export type EntityType = 'Individual' | 'Company' | 'Trust';

export interface Entity {
  id: string;
  name: string;
  type: EntityType;
  taxId?: string; // NIF/NIPC
  email?: string;
}

// --- TENANT CRM ---
export interface Tenant {
  id: string;
  name: string;
  email: string;
  phone: string;
  idNumber?: string; // CC/Passport
  nif?: string;
  status: 'Active' | 'Past' | 'Lead';
  rating?: number; // 1-5 stars
  notes?: string;
  currentPropertyId?: string;
  leaseStart?: string;
  leaseEnd?: string;
  depositAmount?: number;
  rentAmount?: number;
  paymentDay?: number;
}

// --- UPDATED PROPERTY ---
export interface Property {
  id: string;
  entityId?: string; // Link to Owner Entity
  address: string;
  city: string;
  zipCode?: string;
  purchasePrice: number;
  currentValue: number;
  loanAmount?: number;
  interestRate?: number;
  monthlyRent: number;
  expenses: number;
  status: PropertyStatus;
  strategy: InvestmentStrategy;
  imageUrl: string;
  // Legacy fields kept for compatibility, but should sync with Tenant object
  leaseEnd?: string;
  tenantName?: string; 
  tenantId?: string; // Link to Tenant CRM
  lat?: number;
  lng?: number;
  isDeleted?: boolean;
  insuranceExpiry?: string; // New: For Watchtower
}

// --- WATCHTOWER NOTIFICATIONS ---
export type NotificationType = 'Critical' | 'Warning' | 'Info' | 'Success';
export type NotificationCategory = 'Lease' | 'Tax' | 'Insurance' | 'System';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  date: string;
  type: NotificationType;
  category: NotificationCategory;
  isRead: boolean;
  actionLink?: string;
}

// --- EXISTING TYPES BELOW ---

export enum DealStatus {
  New = 'New',
  Analyzing = 'Analyzing',
  OfferSent = 'Offer Sent',
  UnderContract = 'Under Contract',
  Closed = 'Closed',
  Lost = 'Lost'
}

export interface Deal {
  id: string;
  address: string;
  city: string;
  askingPrice: number;
  offerPrice?: number;
  estRent: number;
  estRenovation: number;
  status: DealStatus;
  notes?: string;
  imageUrl?: string;
  dateAdded: string;
}

export interface TaxResult {
  imt: number;
  stampDuty: number;
  imi: number;
  totalUpfront: number;
}

export enum TaskPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High'
}

export enum TaskStatus {
  Pending = 'Pending',
  InProgress = 'In Progress',
  Completed = 'Completed'
}

export interface MaintenanceTask {
  id: string;
  propertyId: string;
  title: string;
  description: string;
  priority: TaskPriority;
  status: TaskStatus;
  dateCreated: string;
  costEstimate?: number;
}

export enum RenovationStatus {
  Planned = 'Planned',
  InProgress = 'In Progress',
  Completed = 'Completed',
  OnHold = 'On Hold'
}

export enum RenovationType {
  Cosmetic = 'Cosmetic',
  Structural = 'Structural',
  FullRemodel = 'Full Remodel',
  Extension = 'Extension'
}

export interface Contractor {
  id: string;
  name: string;
  specialty: string;
  email?: string;
  phone?: string;
  hourlyRate?: number;
  notes?: string;
  rating?: number;
}

export enum ExpenseCategory {
  Labor = 'Labor',
  Materials = 'Materials',
  Permits = 'Permits',
  Equipment = 'Equipment',
  Other = 'Other'
}

export interface RenovationExpense {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: ExpenseCategory;
  contractorId?: string;
}

export interface RenovationQuote {
  id: string;
  providerName: string;
  totalAmount: number;
  includes: string[];
  estimatedDurationDays: number;
  isSelected: boolean;
  fileUrl?: string;
}

export interface RenovationTask {
  id: string;
  title: string;
  phase?: string;
  contractorId?: string;
  startDate?: string;
  endDate?: string;
  status: TaskStatus;
  estimatedCost: number;
  actualCost: number;
  notes?: string;
  quotes?: RenovationQuote[];
}

export interface InspectionItem {
  id: string;
  category: string;
  description: string;
  isPassed: boolean;
  notes?: string;
  imageUrl?: string;
  photos: string[];
}

export interface Budget {
  id: string;
  contractorId: string;
  contractorName: string;
  totalAmount: number;
  estimatedDurationWeeks: number;
  items: { taskId: string; cost: number }[];
  isSelected: boolean;
  notes?: string;
}

export interface RenovationProject {
  id: string;
  propertyId: string;
  name: string;
  type: RenovationType;
  status: RenovationStatus;
  startDate: string;
  endDateEstimated: string;
  endDateActual?: string;
  budget: number;
  description: string;
  tasks: RenovationTask[];
  expenses: RenovationExpense[];
  inspectionList?: InspectionItem[];
  budgets?: Budget[];
}

export interface AIConstructionEstimate {
  low: number;
  high: number;
  average: number;
  breakdown: { category: string; cost: number }[];
  contingency: number;
  durationWeeks: number;
}

export type PageView = 'dashboard' | 'properties' | 'tenants' | 'deals' | 'renovations' | 'maintenance' | 'analytics' | 'reports';

export interface Translation {
  [key: string]: {
    en: string;
    pt: string;
  };
}